import { useContext, useEffect, useState } from "react";
import * as turf from "@turf/turf";

// context
import MapContext from "../../../services/context/map-context";

// utils
import {
  addClipLayer,
  addLayerIfNotExists,
  addOrUpdateSource,
  addPropertyAreaLabel,
  addPropertyDistanceLabels,
  addPropertyVertexLabels,
  addPropertyVertexPointLayer,
  isMapExist,
  removeAllPropertyLabels,
  removeClipLayer,
  removeLayerAndSource,
  removePropertyVertexPointLayer,
} from "../../../utils/map-layer";
import { createDistanceAndVertexFeatures } from "../../../utils/map-labels";
import { polygonToLines } from "../../../utils/map-polygon";
import { formatArea } from "../../../utils/formatting";
import { useUserContext } from "../../../services/context/user-context";
// icons
import triangle from "../../../assets/img/triangle.png";
import { useAppContext } from "../../../services/context/app-context";

export const SelectAccess = ({
  propertyGeoJSON,
  selectedLine,
  updatePropertyData,
}) => {
  const { map, mapLoaded } = useContext(MapContext);
  const { viewOnly } = useUserContext();
  const { showBaseBuildings } = useAppContext();

  const [edgesLayerAdded, setEdgesLayerAdded] = useState(false);
  const [selectedLineId, setSelectedLineId] = useState(
    selectedLine ? selectedLine.properties.id : null
  );
  const [hoveredLineId, setHoveredLineId] = useState(null);

  useEffect(() => {
    if (map && mapLoaded && propertyGeoJSON && !edgesLayerAdded) {
      if(!showBaseBuildings){
        addClipLayer(map, propertyGeoJSON);
      }
      
      addOrUpdateSource(map, "property-polygon", propertyGeoJSON);
      addLayerIfNotExists(map, {
        id: "property-polygon",
        type: "fill",
        source: "property-polygon",
        paint: {
          "fill-color": "#5E6278",
          "fill-opacity": 0.6,
          /* "fill-outline-color": "#343745", */
        },
      });

      setEdgesLayerAdded(true);
      addEdgesLayer(propertyGeoJSON);
      displayVertexAndDistanceLabels(propertyGeoJSON.features[0]);
    }

    // Cleanup on unmount
    return () => {
      if (map &&  isMapExist(map) && edgesLayerAdded) {
        if (map.getLayer("edges-layer")) {
          map.off("mousedown", "edges-layer", onLineClick);
          map.off("mouseenter", "edges-layer", onMouseEnter);
          map.off("mouseleave", "edges-layer", onMouseLeave);
        }
        removeLayerAndSource(map, "property-polygon", "property-polygon");
        removeLayerAndSource(map, "edges-layer", "edges");
        removeLayerAndSource(
          map,
          "property-entry-side-layer",
          "property-entry-side-source"
        );
        if (map.hasImage("triangle-img")) {
          map.removeImage("triangle-img");
        }

        removePropertyVertexPointLayer(map);
        removeAllPropertyLabels(map);
        removeClipLayer(map);
        setEdgesLayerAdded(false);
      }
    };
  }, [map, mapLoaded, propertyGeoJSON, edgesLayerAdded]);

      // hide base buildings
  useEffect(() => {
    if (!showBaseBuildings && propertyGeoJSON) {
      addClipLayer(map, propertyGeoJSON);
    }
    if (showBaseBuildings) {
      removeClipLayer(map);
    }
  }, [showBaseBuildings]);

  // Repaint lines on hover/selected changes
  useEffect(() => {
    if (map && map.getLayer("edges-layer")) {
      map.setPaintProperty("edges-layer", "line-color", [
        "case",
        ["==", ["get", "id"], selectedLineId],
        "#efa21e",
        ["==", ["get", "id"], hoveredLineId],
        "#efa21e",
        "#5E6278",
      ]);
    }
    if (selectedLine) {
      addOrUpdateSource(map, "property-entry-side-source", selectedLine);

      map.loadImage(triangle, (error, image) => {
        if (error) throw error;
        if (!map.hasImage("triangle-img")) {
          map.addImage("triangle-img", image);
        }

        addLayerIfNotExists(map, {
          id: "property-entry-side-layer",
          type: "symbol",
          source: "property-entry-side-source",
          layout: {
            "symbol-placement": "line",
            "symbol-spacing": 50,
            "icon-anchor": "top",
            "icon-size": 0.2,
            "icon-rotate": 180,
            "icon-image": "triangle-img",
            "icon-allow-overlap": false,
            "icon-rotation-alignment": "map",
          },
        });
      });
    }
  }, [map, selectedLineId, hoveredLineId]);

  const addEdgesLayer = (propertyGeoJSON) => {
    const lineFeatures = polygonToLines(propertyGeoJSON.features[0]);
    const edgesGeoJSON = {
      type: "FeatureCollection",
      features: lineFeatures,
    };

    addOrUpdateSource(map, "edges", edgesGeoJSON);
    addLayerIfNotExists(map, {
      id: "edges-layer",
      type: "line",
      source: "edges",
      layout: { "line-join": "round", "line-cap": "round" },
      paint: {
        "line-color": "#5E6278", // not selected
        "line-width": 8,
        "line-opacity": 1,
      },
    });

    // Interactions
    if (!viewOnly) {
      map.on("mousedown", "edges-layer", onLineClick);
      map.on("mouseenter", "edges-layer", onMouseEnter);
      map.on("mouseleave", "edges-layer", onMouseLeave);
      map.getCanvas().style.cursor = "pointer";
    }
  };

  // Show distance + vertex labels
  const displayVertexAndDistanceLabels = (polygon) => {
    const { distanceFeatures, vertexFeatures } =
      createDistanceAndVertexFeatures(polygon);
    const area = turf.area(polygon);
    const centroid = turf.center(polygon);

    addPropertyDistanceLabels(map, {
      type: "FeatureCollection",
      features: distanceFeatures,
    });

    addPropertyVertexLabels(map, {
      type: "FeatureCollection",
      features: vertexFeatures,
    });

    const explodedFC = turf.explode(polygon);
    addPropertyVertexPointLayer(map, explodedFC);

    addPropertyAreaLabel(map, {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          geometry: centroid.geometry,
          properties: {
            area: `${formatArea(area).value} ${formatArea(area).unit}`,
          },
        },
      ],
    });
  };

  // Hover / click event handlers
  const onLineClick = (e) => {
    if (e.features && e.features.length > 0) {
      const feature = e.features[0];
      const lineId = feature.properties.id;
      updatePropertyData("selectedLine", feature);
      setSelectedLineId(lineId);
    }
  };

  const onMouseEnter = (e) => {
    if (e.features && e.features.length > 0) {
      if (hoveredLineId !== e.features[0].properties.id) {
        const hoveredId = e.features[0].properties.id;
        setHoveredLineId(hoveredId);
      }
    }
  };

  const onMouseLeave = () => {
    setHoveredLineId(null);
  };

  return null;
};
